import { isDefined } from '@sgme/fp';
import type { Action } from 'state/actions';
import { addKey, patchKey, removeKey, renameKey } from 'state/utils/collection';

import * as AddonActionTypes from '../../addonForm/addonForm.actionTypes';
import * as BatchActionTypes from '../../batchForm/batchForm.actionTypes';
import * as DynamicActionTypes from '../../dynamic/dynamicForm.actionTypes';
import * as ProductModificationActionTypes from '../../productModificationForm/productModificationForm.actionTypes';
import type { TabsForWorkspace, WorkspaceType } from '../model';
import type {
  WorkspacePersistedTabsApplied,
  WorkspaceResetTabCompleted,
  WorkspaceTabsAllRemoved,
} from '../workspace.actions';
import type { WorkspaceTab, WorkspaceTabStateMap } from './tabs.model';
import { TabLoadingStatus } from './tabs.model';

const reducerName = 'workspaces/tabsReducer';

export function tabsReducer(state: WorkspaceTabStateMap = {}, action: Action): WorkspaceTabStateMap {
  switch (action.type) {
    case 'WORKSPACE_TAB_CREATED':
      return onWorkspaceTabCreated(state, action, action.id, action.workspace, action.tabType, action.tabName);
    case 'WORKSPACE_ADVISORY_TAB_CREATED':
      return onWorkspaceTabCreated(state, action, action.id, action.workspace, action.tabType, action.tabName);
    case 'WORKSPACE_TAB_CLONED':
      const clone: WorkspaceTab = {
        id: action.newTabId,
        type: action.tabType,
        workspace: action.workspace,
        count: getLastWorkspaceIndex(state, action.workspace) + 1,
        loadingStatus: hasLoadingStatus(action.workspace, action.tabType) ? TabLoadingStatus.Loading : undefined,
        isBusy: false,
      };
      const stateWithClonedTab = addKey(state, action.newTabId, clone, { action, reducer: reducerName });
      return patchKey(
        stateWithClonedTab,
        action.oldTabId,
        () => ({
          loadingStatus: TabLoadingStatus.HasBeenCloned,
        }),
        { action, reducer: 'tabsReducer' },
      );
    case 'WORKSPACE_TAB_CLONE_COMPLETED':
      return patchKey(
        state,
        action.sourceTabId,
        () => ({
          loadingStatus: TabLoadingStatus.Loaded,
        }),
        { action, reducer: 'tabsReducer' },
      );
    case 'WORKSPACE_QUOTE_RELOADED':
      const newTab: WorkspaceTab = {
        id: action.temporaryFormId,
        type: 'quote',
        workspace: action.workspace,
        count: getLastWorkspaceIndex(state, action.workspace) + 1,
        loadingStatus: hasLoadingStatus(action.workspace, 'quote') ? TabLoadingStatus.Loading : undefined,
        isBusy: false,
      };
      return addKey(state, action.temporaryFormId, newTab, { action, reducer: reducerName });
    case 'WORKSPACE_TAB_QUOTE_REPRICED':
      return onWorkspaceTabCreated(state, action, action.tabId, action.workspace, 'reprice', action.externalQuoteId);
    case 'WORKSPACE_RFQ_LOAD_REQUESTED':
      return onWorkspaceTabCreated(state, action, action.tabId, action.workspace, 'quote');
    case 'WORKSPACE_TAB_RENAMED':
      return patchKey(state, action.id, tab => ({ ...tab, name: action.name }), { action, reducer: 'tabsReducer' });
    case 'WORKSPACE_TAB_REMOVED':
      return removeKey(state, action.id);
    case 'WORKSPACE_TABS_ALL_REMOVED':
      return onWorkspaceTabsAllRemoved(state, action);
    case 'WORKSPACE_RESET_TAB_COMPLETED':
      return onWorkspaceResetTabCompleted(state, action);
    case 'WORKSPACE_PERSISTED_TABS_APPLIED':
      return onWorkspacePersistedTabsApplied(state, action);
    case 'WORKSPACE_TAB_CONTENT_FETCH_REQUESTED':
      return patchKey(
        state,
        action.tabId,
        tab => ({
          ...tab,
          loadingStatus: hasLoadingStatus(action.workspace, action.tabType) ? TabLoadingStatus.Loading : undefined,
          isBusy: false,
        }),
        { action, reducer: 'tabsReducer' },
      );
    case 'FORM_ENRICHMENT_REQUESTED':
    case 'FORM_INPUT_DATA_CHANGED':
      if (action.workspace === 'addonManagement') {
        return state;
      }
      return patchKey(
        state,
        action.tabId,
        tab => ({
          ...tab,
          loadingStatus: TabLoadingStatus.Loading,
          isBusy: false,
        }),
        { action, reducer: 'tabsReducer' },
      );
    case 'PRODUCT_CODES_REQUESTED':
      return patchKey(
        state,
        action.tabId,
        tab => ({
          ...tab,
          loadingStatus: TabLoadingStatus.Loading,
          isBusy: false,
        }),
        { action, reducer: 'tabsReducer' },
      );
    case 'FORM_ENRICHED_DATA_RECEIVED':
      if (action.workspace === 'addonManagement') {
        return state;
      }
      return patchKey(state, action.tabId, tab => ({ ...tab, loadingStatus: TabLoadingStatus.Loaded, isBusy: false }), {
        action,
      });
    case 'WORKSPACE_TAB_CONTENT_FETCHED':
    case 'PRODUCT_CODES_REJECTED':
      return patchKey(state, action.tabId, tab => ({ ...tab, loadingStatus: TabLoadingStatus.Loaded, isBusy: false }), {
        action,
        reducer: 'tabs.reducer',
      });
    case 'WORKSPACE_TAB_CONTENT_FETCH_FAILED':
      return patchKey(
        state,
        action.tabId,
        tab => ({
          ...tab,
          loadingStatus: TabLoadingStatus.LoadingFailed,
          isBusy: false,
        }),
        { action, reducer: 'tabsReducer' },
      );
    case 'DEFAULT_FORM_FAILED':
      return patchKey(
        state,
        action.tabId,
        tab => ({
          ...tab,
          error: action.error,
          traceId: action.traceId,
          loadingStatus: TabLoadingStatus.NotLoaded,
          isBusy: false,
        }),
        { action, reducer: 'tabsReducer' },
      );
    case 'FORM_RELOAD_FAILED':
      return patchKey(
        state,
        action.tabId,
        tab => ({
          ...tab,
          error: action.error ?? 'Unknown error',
          loadingStatus: TabLoadingStatus.NotLoaded,
          isBusy: false,
        }),
        { action, reducer: 'tabsReducer' },
      );

    case 'WORKSPACE_TAB_FROZEN':
      return patchKey(state, action.tabId, tab => ({ ...tab, isBusy: action.isBusy, isFrozen: true }), {
        action,
        reducer: 'tabsReducer',
      });
    case 'WORKSPACE_TAB_UNFROZEN':
      return patchKey(state, action.tabId, tab => ({ ...tab, isBusy: action.isBusy, isFrozen: false }), {
        action,
        reducer: 'tabsReducer',
      });
    case 'WORKSPACE_TAB_SET_BUSY':
      return patchKey(state, action.tabId, tab => ({ ...tab, isBusy: action.isBusy }), {
        action,
        reducer: 'tabsReducer',
      });
    case 'QUOTE_TRADE_SENT':
    case ProductModificationActionTypes.PRODUCT_MODIFICATION_APPLY_REQUESTED:
      return patchKey(state, action.tabId, tab => ({ ...tab, isBusy: true, isFrozen: true }), {
        action,
        reducer: 'tabsReducer',
      });

    case 'QUOTE_REQUEST_FAILED':
    case 'QUOTE_RESULT_FAILED':
    case 'QUOTE_TRADE_FAILED':
    case 'QUOTE_TRADE_REJECTED':
    case 'QUOTE_TRADE_REQUEST_REJECTED':
    case DynamicActionTypes.DYNAMIC_BOOKING_REQUEST_FAILED:
      return patchKey(state, action.tabId, () => ({ isBusy: false, isFrozen: false }), {
        action,
        reducer: 'tabsReducer',
      });

    case 'QUOTE_RECEIVED':
      return patchKey(
        state,
        action.tabId,
        () => ({ isBusy: false, isFrozen: isDefined(action.quote.rejections) && action.quote.rejections.length > 0 }),
        {
          action,
          reducer: 'tabsReducer',
        },
      );
    case 'QUOTE_REMOVED':
    case 'QUOTE_TRADE_DONE':
    case DynamicActionTypes.DYNAMIC_BOOKING_TRADE_DONE:
    case ProductModificationActionTypes.PRODUCT_MODIFICATION_RESULT_RECEIVED:
    case ProductModificationActionTypes.PRODUCT_MODIFICATION_APPLY_REQUEST_FAILED:
    case ProductModificationActionTypes.PRODUCT_MODIFICATION_RESULT_FAILED:
    case 'QUOTE_STREAMING_TRADE_DONE':
      return patchKey(state, action.tabId, () => ({ isBusy: false, isFrozen: false }), {
        action,
        reducer: 'tabsReducer',
      });
    case 'WORKSPACE_TAB_PRODUCT_SELECTION_SUBMITTED':
      return patchKey(
        state,
        action.tabId,
        tab => ({
          ...tab,
          error: undefined,
          loadingStatus: hasLoadingStatus('static', 'quote') ? TabLoadingStatus.Loading : undefined,
        }),
        { action, reducer: 'tabsReducer' },
      );
    case 'WORKSPACE_RESET_TAB_REQUESTED':
      return patchKey(
        state,
        action.tabId,
        tab => ({
          ...tab,
          error: undefined,
          loadingStatus: hasLoadingStatus(action.workspace, 'quote') ? TabLoadingStatus.Loading : undefined,
        }),
        { action, reducer: 'tabsReducer' },
      );

    case 'FORM_ENRICHMENT_FAILED':
    case BatchActionTypes.BATCH_FORM_ENRICHMENT_FAILED:
      return patchKey(
        state,
        action.tabId,
        tab => ({
          ...tab,
          loadingStatus: TabLoadingStatus.NotLoaded,
          isBusy: false,
        }),
        { action, reducer: 'tabsReducer' },
      );

    /// ADDON FORM, PRODUCT MODIFICATION, TRADE ALLOCATION
    case AddonActionTypes.ADDON_FORM_REQUESTED:
    case AddonActionTypes.ADDON_FORM_RESET_REQUESTED:
    case ProductModificationActionTypes.PRODUCT_MODIFICATION_FORM_REQUESTED:
    case ProductModificationActionTypes.PRODUCT_MODIFICATION_RELOAD_FORM_REQUESTED:
    case 'TRADE_ALLOCATION_FORM_REQUESTED':
      return patchKey(
        state,
        action.tabId,
        tab => ({
          ...tab,
          loadingStatus: tab.loadingStatus || TabLoadingStatus.Loading,
          isBusy: false,
        }),
        { action, reducer: 'tabsReducer' },
      );
    case AddonActionTypes.ADDON_CLIENT_CHANGED:
      return patchKey(
        state,
        action.tabId,
        tab => ({
          ...tab,
          loadingStatus: TabLoadingStatus.Loading,
          isBusy: false,
        }),
        { action, reducer: 'tabsReducer' },
      );
    case AddonActionTypes.ADDON_FORM_RECEIVED:
    case AddonActionTypes.ADDON_CUSTODIANS_RELOADED:
    case AddonActionTypes.ADDON_CUSTODIANS_RELOAD_FAILED:
    case ProductModificationActionTypes.PRODUCT_MODIFICATION_FORM_RECEIVED:
    case 'TRADE_ALLOCATION_FORM_RECEIVED':
    case 'TRADE_ALLOCATION_CUSTODIANS_RELOADED':
      return patchKey(
        state,
        action.tabId,
        tab => ({
          ...tab,
          loadingStatus: TabLoadingStatus.Loaded,
          isBusy: false,
        }),
        { action, reducer: 'tabsReducer' },
      );
    case AddonActionTypes.ADDON_FORM_REQUEST_FAILED:
    case AddonActionTypes.ADDON_FORM_REQUEST_TIMED_OUT:
    case ProductModificationActionTypes.PRODUCT_MODIFICATION_FORM_REQUEST_FAILED:
    case ProductModificationActionTypes.PRODUCT_MODIFICATION_FORM_REQUEST_TIMED_OUT:
    case 'TRADE_ALLOCATION_FORM_REQUEST_FAILED':
    case 'TRADE_ALLOCATION_FORM_REQUEST_TIMED_OUT':
      return patchKey(
        state,
        action.tabId,
        tab => ({
          ...tab,
          loadingStatus: TabLoadingStatus.NotLoaded,
          isBusy: false,
        }),
        { action, reducer: 'tabsReducer' },
      );
    case AddonActionTypes.ADDON_QUOTE_REQUESTED:
    case AddonActionTypes.ADDON_QUOTE_TRADE_SENT:
    case AddonActionTypes.ADDON_QUOTE_TRADE_REQUESTED:
    case 'TRADE_ALLOCATION_APPLY_REQUESTED':
      return patchKey(
        state,
        action.tabId,
        tab => ({
          ...tab,
          isBusy: true,
        }),
        { action, reducer: 'tabsReducer' },
      );
    case AddonActionTypes.ADDON_QUOTE_RECEIVED:
    case AddonActionTypes.ADDON_QUOTE_REMOVED:
    case AddonActionTypes.ADDON_QUOTE_REQUEST_FAILED:
    case AddonActionTypes.ADDON_QUOTE_RESULT_FAILED:
    case AddonActionTypes.ADDON_QUOTE_TRADE_REJECTED:
    case AddonActionTypes.ADDON_QUOTE_TRADE_FAILED:
    case AddonActionTypes.ADDON_QUOTE_TRADE_DONE:
    case 'TRADE_ALLOCATION_APPLY_RESPONSE_RECEIVED':
    case 'TRADE_ALLOCATION_APPLY_REQUEST_FAILED':
      return patchKey(
        state,
        action.tabId,
        tab => ({
          ...tab,
          isBusy: false,
        }),
        { action, reducer: 'tabsReducer' },
      );
    // --- NEW SECONDARY MARKET WORKSPACE --
    case 'SECONDARY_MARKET_QUOTE_REQUESTED':
      return patchKey(state, action.tabId, () => ({ isBusy: true, isFrozen: true }), {
        action,
        reducer: 'tabsReducer',
      });
    case 'SECONDARY_MARKET_QUOTE_CANCELLED':
    case 'SECONDARY_MARKET_QUOTE_FAILED':
    case 'SECONDARY_MARKET_QUOTE_REJECTED':
      return patchKey(state, action.tabId, () => ({ isBusy: false, isFrozen: false }), {
        action,
        reducer: 'tabsReducer',
      });

    case 'SECONDARY_MARKET_TRADE_REJECTED':
    case 'SECONDARY_MARKET_TRADE_FAILED':
    case 'SECONDARY_MARKET_CP_ORDER_PLACEMENT_FAILED':
    case 'SECONDARY_MARKET_LIVE_TRADE_FAILED':
    case 'SECONDARY_MARKET_LIVE_TRADE_REJECTED':
    case 'SECONDARY_MARKET_LIVE_QUOTE_REJECTED':
    case 'SECONDARY_MARKET_LIVE_QUOTE_FAILED':
      return patchKey(state, action.tabId, () => ({ isBusy: false, isFrozen: false }), {
        action,
        reducer: 'tabsReducer',
      });

    case 'SECONDARY_MARKET_TRADE_REQUESTED':
      return patchKey(state, action.tabId, () => ({ isBusy: true }), { action, reducer: 'tabsReducer' });
    case 'SECONDARY_MARKET_TRADE_RECEIVED':
      return patchKey(state, action.tabId, () => ({ isBusy: false }), { action, reducer: 'tabsReducer' });

    default:
      return state;
  }
}

function getLastWorkspaceIndex(state: WorkspaceTabStateMap, workspace: WorkspaceType): number {
  return Object.values(state)
    .filter(tab => tab.workspace === workspace)
    .map(tab => tab.count)
    .reduce((max, current) => (current > max ? current : max), 0);
}

function onWorkspaceTabCreated<W extends WorkspaceType>(
  state: WorkspaceTabStateMap,
  action: Action,
  newId: string,
  workspace: W,
  tabType: TabsForWorkspace[W],
  name?: string,
) {
  const maxWorkspaceCount = getLastWorkspaceIndex(state, workspace);
  const tab: WorkspaceTab = {
    id: newId,
    type: tabType,
    workspace,
    count: maxWorkspaceCount + 1,
    name,
    isBusy: false,
    loadingStatus:
      hasLoadingStatus(workspace, tabType) && workspace !== 'secondaryMarketQuotes'
        ? TabLoadingStatus.Loaded
        : undefined,
  };

  return addKey(state, newId, tab, { action, reducer: 'tabsReducer' });
}

function onWorkspaceTabsAllRemoved(state: WorkspaceTabStateMap, { workspace }: WorkspaceTabsAllRemoved) {
  return Object.keys(state).reduce<WorkspaceTabStateMap>((newState, pricerId) => {
    const pricer = state[pricerId];
    return pricer.workspace === workspace ? newState : { ...newState, [pricerId]: pricer };
  }, {});
}

function onWorkspaceResetTabCompleted(state: WorkspaceTabStateMap, action: WorkspaceResetTabCompleted) {
  return renameKey(
    patchKey(
      state,
      action.id,
      () => ({
        id: action.newId,
        productType: undefined,
        productSubtype: undefined,
        productDefinition: undefined,
        formIds: [],
        nbLoadingForms: 0,
      }),
      { action, reducer: 'tabsReducer' },
    ),
    action.id,
    action.newId,
  );
}

function onWorkspacePersistedTabsApplied(
  state: WorkspaceTabStateMap,
  { tabs }: WorkspacePersistedTabsApplied,
): WorkspaceTabStateMap {
  const tabsToAdd: WorkspaceTabStateMap = Object.values(tabs).reduce<WorkspaceTabStateMap>((acc, tab) => {
    return {
      ...acc,
      [tab.id]: tab,
    };
  }, {});
  return {
    ...state,
    ...tabsToAdd,
  };
}

const hasLoadingStatus = <W extends WorkspaceType>(workspace: W, tabType: TabsForWorkspace[W]) => {
  if (
    workspace === 'static' ||
    workspace === 'dynamic' ||
    workspace === 'secondaryMarketQuotes' ||
    workspace === 'essential' ||
    workspace === 'smartQuote'
  ) {
    return true;
  }

  if (workspace === 'postTrade') {
    if (tabType === 'addOn') {
      return true;
    }
    if (tabType === 'productModification') {
      return true;
    }
  }

  return false;
};
